import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { inject } from 'mobx-react';
import { Alert, Card } from 'react-bootstrap';
import Spinner from '../components/Spinner';
// The full lockup, not the icon: the login card is the one place the
// product introduces itself, and the white card gives it clear space.
import HGLogo from '../assets/images/hg-logo-blue.svg';
import { getStoredState } from '../utils/oauth';
import type Store from '../../store';
import type { Permission } from '../../models/Permissions';

/**
 * Backend response from token exchange
 */
interface ExchangeResponse {
  user: {
    /** MadKudu user ObjectId, used as the Segment userId (CPF-1199). */
    _id?: string;
    email?: string;
    permissions?: Permission[];
    superKudu?: boolean;
  };
}

interface OAuthCallbackProps {
  store?: Store;
}

/**
 * OAuthCallback Component
 *
 * Handles the OAuth 2.0 Authorization Code callback from mk-auth.
 * This component:
 * 1. Extracts authorization code and state from URL query parameters
 * 2. Validates state matches localStorage value (CSRF protection)
 * 3. Exchanges authorization code for access/refresh tokens via backend
 * 4. Stores tokens in localStorage
 * 5. Fetches user IAM data and hydrates store
 * 6. Redirects user to first tenant or original destination
 *
 * Error scenarios handled:
 * - OAuth errors from mk-auth (error parameter in URL)
 * - Missing authorization code
 * - State validation failure (CSRF attack or browser history)
 * - Network errors during token exchange
 * - Invalid/expired authorization codes
 * - Backend error responses
 *
 * @example
 * Callback URL: http://localhost:3013/oauth/callback?code=abc123&state=xyz789
 */
const OAuthCallback = ({ store }: OAuthCallbackProps) => {
  const location = useLocation();
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function handleCallback() {
      try {
        // Parse query parameters
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const oauthError = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors from mk-auth
        if (oauthError) {
          console.error('[OAuth Callback] Error from mk-auth:', {
            error: oauthError,
            description: errorDescription,
          });
          setError(
            errorDescription ||
              `OAuth error: ${oauthError}. Please try logging in again.`
          );
          setLoading(false);
          // Redirect to login after 3 seconds
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
          return;
        }

        // Validate authorization code is present
        if (!code) {
          console.error('[OAuth Callback] Missing authorization code');
          setError('Missing authorization code. Please try logging in again.');
          setLoading(false);
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
          return;
        }

        // Validate state parameter (CSRF protection)
        const storedState = getStoredState();
        if (!state || !storedState || state !== storedState) {
          console.error('[OAuth Callback] State validation failed', {
            received: state,
            expected: storedState,
          });
          setError(
            'Security validation failed. This may be a CSRF attack or an invalid session. Please try logging in again.'
          );
          setLoading(false);
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
          return;
        }

        console.log('[OAuth Callback] State validated successfully');

        // Exchange authorization code for tokens (via cookies)
        console.log(
          '[OAuth Callback] Exchanging authorization code for tokens'
        );
        const response = await axios.post<ExchangeResponse>(
          '/api/oauth/exchange',
          { code }
        );

        const { user } = response.data;

        // Validate user is present
        if (!user) {
          console.error('[OAuth Callback] Missing user in response');
          setError(
            'Invalid response from server. Please try logging in again.'
          );
          setLoading(false);
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
          return;
        }

        console.log(
          '[OAuth Callback] Authentication successful, tokens set in cookies'
        );

        // Hydrate store with user data
        if (store) {
          store.email = user.email || '';
          store.userId = user._id || '';
          store.userPermissions = user.permissions || [];
          store.isSuperKudu = user.superKudu || false;
          console.log('[OAuth Callback] Store hydrated with user data');
        }

        // Determine redirect URL
        let redirectUrl: string;
        if (store?.firstTenant) {
          redirectUrl = `/tenant/${store.firstTenant}`;
          console.log(
            '[OAuth Callback] Redirecting to first tenant:',
            redirectUrl
          );
        } else {
          // Fallback to saved redirect URL or home
          redirectUrl = localStorage.getItem('mk_auth_redirect_url') || '/';
          console.log(
            '[OAuth Callback] Using fallback redirect URL:',
            redirectUrl
          );
        }
        localStorage.removeItem('mk_auth_redirect_url'); // Clean up

        // Redirect to destination
        window.location.href = redirectUrl;
      } catch (err) {
        console.error('[OAuth Callback] Token exchange failed:', err);

        // Handle axios errors
        if (axios.isAxiosError(err)) {
          if (err.response) {
            // Server responded with error
            const message =
              err.response.data?.message ||
              err.response.data?.error ||
              'Token exchange failed';
            setError(`${message}. Please try logging in again.`);
          } else if (err.request) {
            // Network error
            setError(
              'Network error. Please check your connection and try again.'
            );
          } else {
            // Other axios error
            setError('An unexpected error occurred. Please try again.');
          }
        } else {
          // Non-axios error
          setError('An unexpected error occurred. Please try again.');
        }

        setLoading(false);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    }

    handleCallback();
  }, [location.search, history]);

  return (
    <div className="hg-login tw-flex tw-h-full tw-items-center tw-justify-center">
      <Card className="tw-min-h-[300px] tw-w-[373px] tw-p-3">
        <Card.Body className="text-start">
          <div className="tw-full tw-mb-4 tw-flex tw-flex-col tw-items-center">
            <div className="tw-my-4">
              <img
                className="tw-h-9 tw-w-auto"
                src={HGLogo}
                alt="HG Insights logo"
              />
            </div>
            <h1 className="fw-bolder tw-mt-2 tw-text-[var(--hg-navy)]">
              Data Studio
            </h1>
          </div>

          {loading && !error && (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
              <Spinner />
              <p className="tw-text-center tw-text-[var(--app-muted)]">
                Completing sign in...
              </p>
            </div>
          )}

          {error && (
            <div className="tw-flex tw-flex-col tw-gap-3">
              <Alert variant="danger">
                <Alert.Heading>Authentication Error</Alert.Heading>
                <p className="tw-mb-0">{error}</p>
              </Alert>
              <p className="tw-text-center tw-text-sm tw-text-[var(--app-muted)]">
                Redirecting to login page...
              </p>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default inject('store')(OAuthCallback);
